import { FileWarning, Pencil } from 'lucide-react';
import { useState, type ReactElement } from 'react';

import { InlineError } from '@/components/ui/StatePanel';

import { CAPABILITIES, CAPABILITY_LABELS, ruleSummary, type TypeAccess } from '../document-access';
import { useDocumentAccess } from '../use-document-access';
import { TypeAccessModal } from './TypeAccessModal';
import { skeletonLine } from '@/components/ui/skeleton';
import { errorAdvice } from '@/lib/error-advice';

/** Acceso por tipo de documento (spec 2026-10-14 §8 D9): cada tipo con sus cuatro
 *  acciones y los roles que las tienen, o "Según el rol" si no hay regla. En móvil cada
 *  tipo se lee como tarjeta (`.t-cards`). Solo para quien administra las reglas. */
export function DocumentTypeAccess(): ReactElement {
  const query = useDocumentAccess({ enabled: true });
  const [editing, setEditing] = useState<TypeAccess | null>(null);
  const data = query.data;
  const byId = new Map((data?.roles ?? []).map((r) => [r.id, r]));

  return (
    <div className="card rise" style={{ marginTop: 16 }}>
      <div className="ch">
        <h3>Excepciones por tipo de documento</h3>
        <span className="count">Ajustan la matriz de arriba para tipos concretos</span>
      </div>
      <p
        className="soft"
        style={{ fontSize: 12.5, padding: '0 16px 12px', margin: 0, lineHeight: 1.5 }}
      >
        Sin regla, cada acción sigue el permiso del rol. Con regla, solo los roles indicados la
        tienen en ese tipo, tengan o no el permiso. El Administrador de Calidad conserva siempre
        todas las acciones.
      </p>

      {query.isLoading ? (
        <div className="cb" aria-busy="true" aria-label="Cargando acceso por tipo">
          <div className={skeletonLine({ width: 70 })} />
          <div className={skeletonLine({ width: 50, space: 'top' })} />
          <div className={skeletonLine({ width: 60, space: 'top' })} />
        </div>
      ) : query.isError || data === undefined ? (
        <InlineError icon={<FileWarning />} onRetry={() => void query.refetch()}>
          No se pudo cargar el acceso por tipo. {errorAdvice(query.error)}
        </InlineError>
      ) : data.types.length === 0 ? (
        <div className="cb">
          <p className="soft" style={{ fontSize: 13, margin: 0 }}>
            No hay tipos de documento en la empresa activa. Se crean en Catálogos.
          </p>
        </div>
      ) : (
        <div className="t-scroll">
          <table className="t t-cards">
            <thead>
              <tr>
                <th scope="col">Tipo</th>
                {CAPABILITIES.map((c) => (
                  <th key={c} scope="col">
                    {CAPABILITY_LABELS[c]}
                  </th>
                ))}
                <th scope="col" aria-label="Acciones" />
              </tr>
            </thead>
            <tbody>
              {data.types.map((t) => (
                <tr key={t.id}>
                  <td data-label="Tipo">
                    {t.name}
                    {!t.isActive && (
                      <span className="badge b-mut" style={{ marginLeft: 6 }}>
                        Inactivo
                      </span>
                    )}
                  </td>
                  {CAPABILITIES.map((c) => (
                    <td key={c} data-label={CAPABILITY_LABELS[c]}>
                      <span className={t.rules[c].length === 0 ? 'soft' : undefined}>
                        {ruleSummary(t.rules[c], byId)}
                      </span>
                    </td>
                  ))}
                  <td className="t-act" data-label="Acciones">
                    <div className="row-actions">
                      <button
                        type="button"
                        className="btn mut sm"
                        aria-label={`Editar acceso de ${t.name}`}
                        onClick={() => setEditing(t)}
                      >
                        <Pencil aria-hidden="true" size={14} />
                        Editar
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="soft" style={{ fontSize: 12, padding: '10px 16px 14px', margin: 0 }}>
        Quien ya recibió la capacitación de un documento lo sigue viendo en el LMS aunque una regla
        posterior le quite la consulta en el QMS.
      </p>

      {editing !== null && data !== undefined && (
        <TypeAccessModal type={editing} roles={data.roles} onClose={() => setEditing(null)} />
      )}
    </div>
  );
}
